import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../contexts/DataContext';
import { calculateTotalEmissionsAndAbsorption } from '../../utils/emissionSegmentUtils';

function EmissionsCard({ iconSrc, label }) {
   const { t } = useTranslation();
  const { data } = useContext(DataContext);

  const { totalEmission, totalAbsorption } = calculateTotalEmissionsAndAbsorption(data);

  // 吸収量を基準にした排出量の高さを計算
  const baseHeight = 100; // 吸収量を基準とした高さ
  const adjustedEmissionHeight = totalAbsorption > 0 ? baseHeight * (totalEmission / totalAbsorption) : baseHeight;

  // 各セグメントの排出量を JSON データから取得し、スタイルを設定
  const emissionSegments = [
    { color: 'var(--segment-green)', value: parseFloat(data.SupplyChainInfo.rawMaterial.co2Emission) || 0 },
    { color: 'var(--segment-purple)', value: parseFloat(data.SupplyChainInfo.spinning.co2Emission) || 0 },
    { color: 'var(--segment-yellow)', value: parseFloat(data.SupplyChainInfo.weaving.co2Emission) || 0 },
    { color: 'var(--segment-blue)', value: parseFloat(data.SupplyChainInfo.sewing.co2Emission) || 0 },
    { color: 'var(--segment-turquoise)', value: parseFloat(data.SupplyChainInfo.dyeing.co2Emission) || 0 } 
  ];




  let cumulativeHeight = 0;
  const adjustedSegments = emissionSegments.map((segment, index) => {
    const segmentHeight = (segment.value / totalEmission) * adjustedEmissionHeight;
    const segmentStyle = {
      height: `${segmentHeight}px`,
      backgroundColor: segment.color,
      bottom: `${cumulativeHeight}px`,
      position: 'absolute',
    };
    cumulativeHeight += segmentHeight;
    return { style: segmentStyle, delay: index * 0.3 }; // 各セグメントに遅延を追加
  });

  return (
    <div className="info-card">
      <header className="info-label">
        <span className="icon icon-emissions">
          <img src={iconSrc} alt={label} width="20" height="20" />
        </span>
        {label}
      </header>

      <div className="emissions-grid">
        {/* 左側の排出量の積み上げグラフ */}
        <div className="emissions-column">
          <div className="emissions-bar">
            {adjustedSegments.map(({ style, delay }, index) => (
              <motion.div
                key={index}
                className="co2-emission-segment"
                style={style}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: style.height }}
                transition={{ duration: 0.2, delay, ease: 'anticipate' }} // 各セグメントに個別の遅延を適用
              />
            ))}
          </div>
          <div className="emissions-label-container">
            <span className="icon icon-arrow-up">
              <img src="/images/icon/icon_arrow_up.svg" alt={t('emissionsCard.emission')} width="15" height="15" />
            </span>
            <span className="emissions-label">{t('emissionsCard.emission')}</span>
          </div>
          <div className="emissions-value">
            {totalEmission.toFixed(2)} <span className="emissions-unit">kg-CO<sub>2</sub></span>
          </div>
        </div>

        {/* 右側の吸収量のグラフ */}
        <div className="emissions-column">
          <div className="emissions-bar">
            <motion.div
              className="co2-absorption-bar"
              style={{ backgroundColor: 'var(--segment-green)' }}
              initial={{ height: 0 }}
              animate={{ height: `${baseHeight}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
          <div className="emissions-label-container">
            <span className="icon icon-arrow-down">
              <img src="/images/icon/icon_arrow_down.svg" alt={t('emissionsCard.absorption')} width="15" height="15" />
            </span>
            <span className="emissions-label">{t('emissionsCard.absorption')}</span>
          </div>
          <div className="emissions-value">
            {totalAbsorption.toFixed(2)} <span className="emissions-unit">kg-CO<sub>2</sub></span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmissionsCard;
